<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="860px">
      <v-card>
        <v-card-title>
          <span>
            {{ $t("wkt.admin.vacation-policy.default-policy.dialog.title") }}
          </span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  :value="defaultPolicyName"
                  :label="
                    $t('wkt.admin.vacation-policy.vacation-policy-window.name')
                  "
                  disabled
                />
              </v-col>
            </v-row>

            <v-row style="align-items: center;">
              <v-col cols="6">
                <v-select
                  v-model="form.assistedVacationCountMode"
                  :items="countModeItems"
                  item-text="text"
                  item-value="value"
                  :label="
                    $t(
                      'wkt.admin.vacation-policy.vacation-policy-window.assisted_count_mode',
                    )
                  "
                  :rules="[countModeRule]"
                  :disabled="!canModify"
                />
              </v-col>

              <v-col cols="3">
                <v-text-field
                  v-model="form.defaultYearlyVacationDays"
                  type="number"
                  min="0"
                  max="365"
                  step="1"
                  :label="
                    $t(
                      'wkt.admin.vacation-policy.vacation-policy-window.yearly_vacation_days',
                    )
                  "
                  :rules="[
                    validationRules.required,
                    (value) => annualValueRule(value, 0, 365),
                  ]"
                  :disabled="!canModify"
                />
              </v-col>
              <v-col cols="3">
                <v-btn
                  v-if="canModify"
                  text
                  small
                  color="#185EA9"
                  @click="openResetBalanceDialog"
                >
                  <span
                    style="text-decoration: underline; text-underline-offset: 2px;"
                  >
                    {{
                      $t(
                        "wkt.admin.vacation-policy.tab-general.start_bag_hours_balance",
                      ).toUpperCase()
                    }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>

            <v-row style="position:relative; top:-30px">
              <v-col cols="6">
                <span>
                  {{
                    $t(
                      "wkt.admin.vacation-policy.vacation-policy-window.assisted_count_mode_description",
                    )
                  }}
                </span>
              </v-col>
            </v-row>

            <v-row align="center" style="position:relative; top:-30px">
              <v-col cols="12" md="5">
                <v-switch
                  v-model="form.defaultYearlyVacationAllowNegative"
                  hide-details
                  :label="
                    $t(
                      'wkt.admin.vacation-policy.default-policy.allow_negative',
                    )
                  "
                  :disabled="!canModify"
                  class="vp-switch"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn v-if="canModify" color="#185EA9" text @click="ok">
            {{ $t("wkt.admin.common.dialog-buttons.save").toUpperCase() }}
          </v-btn>
          <v-btn text @click="showDialog = false" class="vp-dialog-button">
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>

        <LoadingDialog :loading="loading" />
      </v-card>
    </v-dialog>

    <VacationPolicyBalanceResetDialog ref="resetDialog" />
  </v-row>
</template>

<script>
import api from "../../../api/worktime";
import BaseModule from "../../common/BaseModule";
import LoadingDialog from "../../common/LoadingDialog";
import deepClone from "../../util/deep-clone";
import VacationPolicyBalanceResetDialog from "./VacationPolicyBalanceResetDialog";

export default {
  name: "VacationPolicyDefaultDialog",
  mixins: [BaseModule],
  components: {
    LoadingDialog,
    VacationPolicyBalanceResetDialog,
  },
  data() {
    return {
      showDialog: false,
      loading: false,
      canModify: false,
      company: null,
      callback: null,
      form: {
        assistedVacationCountMode: 0,
        defaultYearlyVacationDays: 0,
        defaultYearlyVacationAllowNegative: false,
      },
    };
  },
  computed: {
    defaultPolicyName() {
      return this.company && this.company.defaultYearlyVacationLabel
        ? this.company.defaultYearlyVacationLabel
        : this.$t("wkt.admin.vacation-policy.table.days_vacation");
    },
    countModeItems() {
      return [
        {
          value: 0,
          text: this.$t(
            "wkt.admin.vacation-policy.vacation-policy-window.count_mode_none",
          ),
        },
        {
          value: 1,
          text: this.$t(
            "wkt.admin.vacation-policy.vacation-policy-window.count_mode_natural_days",
          ),
        },
        {
          value: 2,
          text: this.$t(
            "wkt.admin.vacation-policy.vacation-policy-window.count_mode_none_work_days",
          ),
        },
      ];
    },
  },
  methods: {
    emitError(message) {
      if (this.callback) {
        this.callback({
          success: false,
          message,
        });
      }
    },
    countModeRule(value) {
      return value !== null && value !== undefined
        ? true
        : this.$t("wkt.admin.common.validation-utils.required_field");
    },
    annualValueRule(value, minimum, maximum) {
      const numericValue = Number(value);

      if (Number.isNaN(numericValue) || !Number.isInteger(numericValue)) {
        return this.$t(
          "wkt.admin.vacation-policy.validation.integer_annual_value",
        );
      }

      return (
        (numericValue >= minimum && numericValue <= maximum) ||
        `${this.$t(
          "wkt.admin.common.validation-utils.required_field",
        )} [ ${minimum} - ${maximum} ]`
      );
    },
    normalizeIntegerValue(value) {
      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? 0 : Math.trunc(numericValue);
    },
    open(company, canModify, callback) {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.company = company ? deepClone(company) : null;
      this.canModify = canModify;
      this.callback = callback;
      this.form = {
        assistedVacationCountMode: this.company
          ? Number(this.company.assistedVacationCountMode || 0)
          : 0,
        defaultYearlyVacationDays: this.company
          ? Number(this.company.defaultYearlyVacationDays || 0)
          : 0,
        defaultYearlyVacationAllowNegative: Boolean(
          this.company && this.company.defaultYearlyVacationAllowNegative,
        ),
      };
      this.showDialog = true;
    },
    buildCompanyPayload() {
      const updatedCompany = deepClone(this.company);

      updatedCompany.assistedVacationCountMode = Number(
        this.form.assistedVacationCountMode || 0,
      );
      updatedCompany.defaultYearlyVacationDays = this.normalizeIntegerValue(
        this.form.defaultYearlyVacationDays,
      );
      updatedCompany.defaultYearlyVacationAllowNegative = Boolean(
        this.form.defaultYearlyVacationAllowNegative,
      );

      return updatedCompany;
    },
    openResetBalanceDialog() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.$refs.resetDialog.open(
        {
          bagLabel:
            this.company && this.company.defaultYearlyVacationLabel
              ? this.company.defaultYearlyVacationLabel
              : this.$t("wkt.admin.vacation-policy.table.days_vacation"),
        },
        (resetOptions) => {
          this.save(resetOptions);
        },
      );
    },
    ok() {
      this.save();
    },
    save(resetOptions = null) {
      if (!this.canModify) {
        this.showDialog = false;
        return;
      }

      if (!this.$refs.form.validate()) {
        return;
      }

      const updatedCompany = this.buildCompanyPayload();
      this.loading = true;

      api.updateMyCompany(
        updatedCompany,
        (response) => {
          if (!response || !response.rsOK) {
            this.loading = false;
            this.emitError(
              response
                ? response.message
                : this.$t("wkt.admin.common.error.save_server"),
            );
            return;
          }

          if (resetOptions) {
            this.executeUpdateBalance(
              updatedCompany,
              resetOptions,
              response.message,
            );
            return;
          }

          this.loading = false;
          this.showDialog = false;

          if (this.callback) {
            this.callback({
              success: true,
              company: updatedCompany,
              message: response.message,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
    executeUpdateBalance(updatedCompany, resetOptions, saveMessage) {
      const filters = api.getCurrentAdminFilters(updatedCompany.id);

      api.updateBalanceVacation(
        {
          workplaceId: filters.workplaceId,
          departmentId: filters.departmentId,
          vacationPolicyId: null,
          hoursBagId: 6,
          hoursBagDays: this.normalizeIntegerValue(
            updatedCompany.defaultYearlyVacationDays,
          ),
          accumulateVacation: Boolean(resetOptions.accumulateVacation),
        },
        (response) => {
          this.loading = false;

          if (!response || !response.rsOK) {
            this.emitError(
              response
                ? response.message
                : this.$t("wkt.admin.common.error.save_server"),
            );
            return;
          }

          this.showDialog = false;

          if (this.callback) {
            this.callback({
              success: true,
              company: updatedCompany,
              message: response.message || saveMessage,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
  },
};
</script>
